import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  SimpleGrid,
  Flex,
  Button,
  Text,
} from '@chakra-ui/react';

import IntegrationsApi from '../../../../../api/integrations';
import { exportErrorsFile } from '../../../../../functions/generatePayrollFile';
import { usePipelineResults } from '../context/PipelineResultsContext';

const PipelineModal = () => {
  const [pipelineInformation, setPipelineInformation] = useState(undefined);
  const [errorsList, setErrorsList] = useState([]);

  const {
    isModalOpen: isOpen,
    onModalClose: onClose,
    toast,
    selectedPipeline,
  } = usePipelineResults();

  useEffect(() => {
    if (isOpen && selectedPipeline) {
      setPipelineInformation(selectedPipeline);
      getPipelineErrorsList(selectedPipeline.id);
    } else {
      setPipelineInformation(undefined);
      setErrorsList([]);
    }
  }, [isOpen, selectedPipeline]);

  const handleDownloadFileClick = () => {
    IntegrationsApi.downloadIntegrationFile(pipelineInformation.id)
      .then((res) => {
        try {
          // The gateway returns the response object, so the actual data is in res.data.data
          const responseData = res.data?.data || res.data;
          let fileContent;
          let fileName = 'pipeline_data';

          if (typeof responseData === 'string') {
            // Check if it's base64 encoded (likely XLSX)
            if (responseData.match(/^[A-Za-z0-9+/]*={0,2}$/) && responseData.length > 100) {
              // Looks like base64 - treat as XLSX
              try {
                const byteCharacters = atob(responseData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                fileName = 'pipeline_data.xlsx';

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
              } catch (base64Error) {
                console.error('Error decoding base64:', base64Error);
                // Fallback to treating as text
                fileContent = responseData;
                const blob = new Blob([fileContent], { type: 'text/plain' });
                fileName = 'pipeline_data.txt';

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
              }
            } else {
              // Treat as JSON string or plain text
              try {
                // Try to parse as JSON to format it nicely
                const jsonData = JSON.parse(responseData);
                fileContent = JSON.stringify(jsonData, null, 2);
                fileName = 'pipeline_data.json';
              } catch {
                // If not valid JSON, use as-is
                fileContent = responseData;
                fileName = 'pipeline_data.txt';
              }

              const blob = new Blob([fileContent], {
                type: fileName.endsWith('.json') ? 'application/json' : 'text/plain',
              });

              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = fileName;
              a.click();
              window.URL.revokeObjectURL(url);
            }
          } else {
            // Handle other data types (arrays, objects)
            fileContent = JSON.stringify(responseData, null, 2);
            const blob = new Blob([fileContent], {
              type: 'application/json',
            });
            fileName = 'pipeline_data.json';

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        } catch (e) {
          console.error('Error downloading file:', e);
          toast({
            title: `Download failed: ${e.message}`,
            position: 'top',
            status: 'error',
            isClosable: false,
          });
        }
      })
      .catch((e) => {
        console.error('API error:', e);
        toast({
          title: `Failed to download file`,
          position: 'top',
          status: 'error',
          isClosable: false,
        });
      });
  };

  const handleRunAgainClick = () => {
    IntegrationsApi.triggerIntegrationAction(pipelineInformation.integrationActionId)
      .then((res) => {
        toast({
          title: `Data Pipeline Started`,
          position: 'top',
          status: 'success',
          isClosable: false,
        });
      })
      .catch((e) => {
        toast({
          title: `Data Pipeline Failure`,
          position: 'top',
          status: 'error',
          isClosable: false,
        });
      });
  };

  const handleDownloadErrorsFileClick = () => {
    exportErrorsFile(errorsList);
  };

  const getPipelineErrorsList = (pipelineId) => {
    IntegrationsApi.getIntegrationsErrors(pipelineId, null)
      .then((res) => {
        if (res.data != null) {
          setErrorsList(res.data);
        }
      })
      .catch((e) => {});
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="1000px" dir="ltr">
        <ModalHeader>Data Pipeline</ModalHeader>
        <ModalBody>
          <SimpleGrid marginTop="30px" columns={{ sm: 1 }} gap="20px" mb="0px">
            <Flex flexDirection="row">
              <Button backgroundColor="green.200" width="200px" onClick={handleRunAgainClick}>
                Run Pipeline
              </Button>

              <Button
                marginLeft="50px"
                backgroundColor="gray.200"
                width="280px"
                onClick={handleDownloadFileClick}
              >
                Download Integration Response
              </Button>

              <Button
                marginLeft="50px"
                backgroundColor="red.200"
                width="200px"
                onClick={handleDownloadErrorsFileClick}
              >
                Download Errors Report
              </Button>
            </Flex>

            <Flex flexDirection="column">
              {errorsList && errorsList.length > 0 && (
                <Text fontWeight="bold" fontSize="18px" textColor="red">
                  Errors
                </Text>
              )}

              {errorsList && errorsList.length > 0 && (
                <div
                  style={{
                    height: '400px',
                    overflow: 'scroll',
                    outline: '0',
                    border: '1px solid #FFFFFF50',
                  }}
                >
                  <Flex flexDirection="column" marginBottom="20px">
                    {'\n'}
                    {errorsList.map((value) => (
                      <h4
                        style={{
                          fontSize: '1em',
                          textAlign: 'right',
                          fontWeight: '600',
                          transition: '.2s all',
                        }}
                      >
                        <br />
                        <Text align="left">{JSON.stringify(value).replace(/\\/g, '')}</Text>
                      </h4>
                    ))}
                  </Flex>
                </div>
              )}
            </Flex>
          </SimpleGrid>

          <br />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PipelineModal;
